import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { CreditCard, X } from 'lucide-react';

const STATUS_TONE = {
  PAID: 'bg-[#167A5A]/10 text-[#167A5A]',
  PENDING_PAYMENT: 'bg-[#B45309]/10 text-[#B45309]',
  HOLD: 'bg-slate-200 text-slate-700',
  CHECKED_IN: 'bg-[#1B3A87]/10 text-[#1B3A87]',
  CHECKED_OUT: 'bg-slate-200 text-slate-700',
  CANCELLED: 'bg-[#B42318]/10 text-[#B42318]',
};

export default function ClientBookings() {
  const [bookings, setBookings] = useState([]);
  const load = () => api.get('/bookings').then(r => setBookings(r.data));
  useEffect(() => { load(); }, []);

  const pay = async (b) => {
    try {
      const { data } = await api.post('/payments/stripe/checkout', { booking_id: b.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const cancel = async (b) => {
    if (!window.confirm('Cancel this booking?')) return;
    await api.delete(`/bookings/${b.id}`); toast.success('Cancelled'); load();
  };

  return (
    <div className='p-6 lg:p-8 max-w-6xl mx-auto'>
      <div className='flex items-center justify-between mb-6'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Reservations</div>
          <h1 className='font-display text-3xl font-bold text-[#0B1220] mt-1'>My Bookings</h1>
        </div>
      </div>

      {bookings.length === 0 ? (
        <Card className='p-10 text-center border border-dashed border-slate-300 text-slate-500'>No bookings yet.</Card>
      ) : (
        <div className='space-y-3' data-testid='bookings-list'>
          {bookings.map((b) => (
            <Card key={b.id} className='p-4 border border-slate-200 hover:shadow-sm transition-shadow' data-testid={`booking-row-${b.id}`}>
              <div className='flex flex-wrap items-center gap-4 justify-between'>
                <div className='flex-1 min-w-[200px]'>
                  <div className='flex items-center gap-2'>
                    <div className='font-display font-semibold text-lg'>{b.dog_name}</div>
                    <Badge className={STATUS_TONE[b.status] || ''}>{b.status.replace('_',' ')}</Badge>
                  </div>
                  <div className='text-xs text-slate-500 mt-0.5'>
                    Run <span className='font-mono'>{b.run_name}</span> ({b.run_type}) · {new Date(b.start).toLocaleDateString()} → {new Date(b.end).toLocaleDateString()} · {b.nights} nights
                  </div>
                </div>
                <div className='text-right'>
                  <div className='text-[10px] uppercase tracking-wider font-bold text-slate-500'>Amount</div>
                  <div className='font-display font-bold text-xl tabular-nums'>${b.amount.toFixed(2)}</div>
                </div>
                <div className='flex gap-2'>
                  {b.status === 'PENDING_PAYMENT' && (
                    <Button size='sm' className='bg-[#C8102E] hover:bg-[#A50D26] text-white' onClick={() => pay(b)} data-testid={`pay-button-${b.id}`}><CreditCard className='w-4 h-4 mr-1' /> Pay now</Button>
                  )}
                  {!['CANCELLED','CHECKED_OUT'].includes(b.status) && (
                    <Button size='sm' variant='ghost' onClick={() => cancel(b)} data-testid={`cancel-button-${b.id}`}><X className='w-4 h-4 text-[#B42318]' /></Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
